import React, { useCallback, useEffect, useRef } from "react";

import { Organization } from "@packages/client";
import { TickIcon } from "@packages/ds";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";

import * as S from "./GroupedOrganizationsList.styles";

type GroupedOrganizationsProps = {
  organizations: Record<string, Array<Organization>>;
  isOpen: boolean;
  onOpenChange: (status: boolean) => void;
};

export const GroupedOrganizationsList = ({
  organizations,
  isOpen,
  onOpenChange
}: GroupedOrganizationsProps) => {
  const organizationListRef = useRef<HTMLDivElement>(null);
  const { organizationId } = useParams();

  useEffect(() => {
    if (isOpen && organizationListRef.current)
      for (const c of organizationListRef.current.children)
        if (c.getAttribute("data-name") === organizationId)
          c.scrollIntoView({ block: "center" });
  }, [isOpen, organizationId]);

  const closeDialog = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <S.OrganizationListContainer as="div" ref={organizationListRef}>
      {Object.entries(organizations)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([type, organizations]) => (
          <React.Fragment key={type}>
            <S.GroupHeader as="li">
              <FormattedMessage id={`organizations_${type}`} />
            </S.GroupHeader>

            {organizations.map(organization => (
              <S.OrganizationListLink
                key={organization.id}
                data-name={organization.name}
                $isActive={organizationId === organization.name}
              >
                <S.OrganizationLink
                  to={`/${organization.name}`}
                  onClick={closeDialog}
                >
                  {organization.label}

                  {organizationId === organization.name && <TickIcon />}
                </S.OrganizationLink>
              </S.OrganizationListLink>
            ))}
          </React.Fragment>
        ))}
    </S.OrganizationListContainer>
  );
};
